import type {
  MarketDataCoverage,
  MarketDataQuality,
  StrategyMarketFamily,
} from "@papertiger/contracts";

/** Default interactive window selected inside known replayable coverage. */
const DEFAULT_BACKTEST_WINDOW_MILLISECONDS = 15 * 60_000;

/** One time window safe to expose in the backtest date controls. */
export interface BacktestAvailabilityWindow {
  /** Earliest selectable minute with replayable L2 data. */
  availableStart: string;
  /** Latest selectable minute with replayable L2 data. */
  availableEnd: string;
  /** Default start selected within the available range. */
  defaultStart: string;
  /** Default end selected within the available range. */
  defaultEnd: string;
  /** Latest collector observation represented by the matching rows. */
  freshestObservedAt: string;
  /** Number of matching market-level coverage records. */
  marketCount: number;
  /** Known collection or sequence gaps across matching markets. */
  gapCount: number;
  /** Least trustworthy quality among the matching rows. */
  quality: MarketDataQuality;
}

/**
 * Derives minute-aligned replayable-L2 bounds for selected venues.
 *
 * Venue summary rows are intentionally excluded because their definition-only
 * history can predate the first executable L2 observation.
 *
 * @param coverage Current market-data coverage rows.
 * @param families Rolling market families used by the editable strategy.
 * @param collectionMode Continuous capture or intentional sampled windows.
 * @returns Selectable bounds and defaults, or null when no full minute exists.
 */
export function calculateBacktestAvailability(
  coverage: readonly MarketDataCoverage[],
  families: readonly StrategyMarketFamily[],
  collectionMode: "continuous" | "sampled" = "continuous",
): BacktestAvailabilityWindow | null {
  const rows = coverage.filter(
    (
      row,
    ): row is MarketDataCoverage & {
      availableStart: string;
      availableEnd: string;
      freshestObservedAt: string;
    } =>
      row.marketId !== null &&
      families.some(
        (family) =>
          row.venue === family.venue &&
          row.underlying === family.underlying &&
          row.durationMinutes === family.durationMinutes,
      ) &&
      row.fidelity === "replayable_l2" &&
      row.availableStart !== null &&
      row.availableEnd !== null &&
      row.freshestObservedAt !== null,
  );
  if (rows.length === 0) {
    return null;
  }
  const selectedRange =
    collectionMode === "sampled"
      ? latestContinuousRangeWithCompleteMarket(rows)
      : {
          start: Math.min(
            ...rows.map((row) => Date.parse(row.availableStart)),
          ),
          end: Math.max(...rows.map((row) => Date.parse(row.availableEnd))),
        };
  if (selectedRange === null) {
    return null;
  }
  const selectedRows = rows.filter(
    (row) =>
      Date.parse(row.availableStart) <= selectedRange.end &&
      Date.parse(row.availableEnd) >= selectedRange.start,
  );
  const selectableStartMilliseconds =
    Math.ceil(selectedRange.start / 60_000) * 60_000;
  const selectableEndMilliseconds =
    Math.floor(selectedRange.end / 60_000) * 60_000;
  if (
    !Number.isFinite(selectableStartMilliseconds) ||
    !Number.isFinite(selectableEndMilliseconds) ||
    selectableStartMilliseconds >= selectableEndMilliseconds
  ) {
    return null;
  }
  const latestCompletedMarketCloseMilliseconds = Math.max(
    ...selectedRows.flatMap((row) => {
      if (!coversCompleteMarketLifecycle(row)) {
        return [];
      }
      return [Date.parse(row.marketClosesAt)];
    }),
  );
  const defaultEndMilliseconds = Number.isFinite(
    latestCompletedMarketCloseMilliseconds,
  )
    ? Math.min(
        selectableEndMilliseconds,
        latestCompletedMarketCloseMilliseconds,
      )
    : selectableEndMilliseconds;
  const defaultStartMilliseconds = Math.max(
    selectableStartMilliseconds,
    defaultEndMilliseconds - DEFAULT_BACKTEST_WINDOW_MILLISECONDS,
  );
  const defaultRows = selectedRows.filter(
    (row) =>
      Date.parse(row.availableStart) <= defaultEndMilliseconds &&
      Date.parse(row.availableEnd) >= defaultStartMilliseconds,
  );
  const freshestObservedAt = selectedRows
    .map((row) => row.freshestObservedAt)
    .sort()
    .at(-1);
  if (freshestObservedAt === undefined) {
    return null;
  }
  return {
    availableStart: new Date(selectableStartMilliseconds).toISOString(),
    availableEnd: new Date(selectableEndMilliseconds).toISOString(),
    defaultStart: new Date(defaultStartMilliseconds).toISOString(),
    defaultEnd: new Date(defaultEndMilliseconds).toISOString(),
    freshestObservedAt,
    marketCount: selectedRows.length,
    gapCount: defaultRows.reduce((total, row) => total + row.gapCount, 0),
    quality: defaultRows.reduce<MarketDataQuality>(
      (quality, row) => worstCoverageQuality(quality, row.quality),
      "complete",
    ),
  };
}

/**
 * Finds the latest contiguous sampled range containing a complete market.
 *
 * Adjacent rolling markets may have sub-minute observation gaps around their
 * scheduled rollover, so intervals separated by at most one minute are joined.
 * A newer partial fragment is not selectable because it can make the default
 * Backtests strategy complete with zero evaluable decisions even when the
 * preceding sampled interval contains a full market lifecycle.
 *
 * @param rows Matching market-level replay coverage.
 * @returns Latest usable contiguous range, or null when none is complete.
 */
function latestContinuousRangeWithCompleteMarket(
  rows: ReadonlyArray<
    MarketDataCoverage & {
      availableStart: string;
      availableEnd: string;
      freshestObservedAt: string;
    }
  >,
): { start: number; end: number } | null {
  const ranges = rows
    .map((row) => ({
      start: Date.parse(row.availableStart),
      end: Date.parse(row.availableEnd),
      hasCompleteMarket: coversCompleteMarketLifecycle(row),
    }))
    .filter(
      (range) =>
        Number.isFinite(range.start) &&
        Number.isFinite(range.end) &&
        range.start < range.end,
    )
    .sort((left, right) => left.start - right.start);
  const first = ranges[0];
  if (first === undefined) {
    return null;
  }
  const merged: Array<{
    start: number;
    end: number;
    hasCompleteMarket: boolean;
  }> = [{ ...first }];
  for (const range of ranges.slice(1)) {
    const current = merged.at(-1);
    if (current === undefined) {
      continue;
    }
    if (range.start <= current.end + 60_000) {
      current.end = Math.max(current.end, range.end);
      current.hasCompleteMarket ||= range.hasCompleteMarket;
    } else {
      merged.push({ ...range });
    }
  }
  const latestUsableRange = merged
    .filter((range) => range.hasCompleteMarket)
    .at(-1);
  return latestUsableRange === undefined
    ? null
    : {
        start: latestUsableRange.start,
        end: latestUsableRange.end,
      };
}

/**
 * Checks whether one market row covers its inferred full rolling lifecycle.
 *
 * @param row Market-level replay coverage.
 * @returns Whether the row is complete, gap-free, and reaches open and close.
 */
function coversCompleteMarketLifecycle(
  row: MarketDataCoverage & {
    availableStart: string;
    availableEnd: string;
  },
): row is MarketDataCoverage & {
  availableStart: string;
  availableEnd: string;
  marketClosesAt: string;
  durationMinutes: number;
} {
  if (
    row.marketClosesAt === null ||
    row.durationMinutes === null ||
    row.quality !== "complete" ||
    row.gapCount !== 0
  ) {
    return false;
  }
  const startMilliseconds = Date.parse(row.availableStart);
  const endMilliseconds = Date.parse(row.availableEnd);
  const closeMilliseconds = Date.parse(row.marketClosesAt);
  const openMilliseconds =
    closeMilliseconds - row.durationMinutes * 60_000;
  return (
    Number.isFinite(startMilliseconds) &&
    Number.isFinite(endMilliseconds) &&
    Number.isFinite(closeMilliseconds) &&
    startMilliseconds <= openMilliseconds + 2_000 &&
    endMilliseconds >= closeMilliseconds - 2_000
  );
}

/**
 * Selects the less trustworthy of two coverage classifications.
 *
 * @param left First quality.
 * @param right Second quality.
 * @returns Worst quality.
 */
function worstCoverageQuality(
  left: MarketDataQuality,
  right: MarketDataQuality,
): MarketDataQuality {
  const rank: Record<MarketDataQuality, number> = {
    complete: 0,
    stale: 1,
    partial: 2,
    gapped: 3,
  };
  return rank[left] >= rank[right] ? left : right;
}
